import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Field,
  Form,
  FormFooter,
  FormRow,
  FormSection,
  SearchSelect,
} from '@evogard/ui';
import {
  applyInputMask,
  parseMoneyInput,
  toColumn,
  type AssistancePricing,
  type ProviderPaymentMode,
} from '@evogard/domain';
import type {
  AssistancePriceRecord,
  AssistanceProvider,
  AssistanceProviderServicesWrite,
} from '@evogard/contract';
import { api, ok } from '../../../lib/api.js';
import { can, useSessionContext } from '../../../lib/session.js';
import { COMPANIES_KEY, type Company } from './useCompanies.js';

/** A mesma chave que a mesa do acionamento invalida — a cotação lê os prestadores. */
const PROVIDERS_KEY = ['assistencia', 'prestadores'] as const;
/**
 * O catálogo da assistência, com a mesma chave de `modules/assistance/catalog.ts`
 * — repetida aqui, e não importada, porque módulo não importa de outro módulo.
 */
const CATALOG_KEY = ['assistencia', 'servicos'] as const;

function useAssistanceCatalog() {
  return useQuery({
    queryKey: CATALOG_KEY,
    staleTime: 5 * 60_000,
    queryFn: async () =>
      ok(
        await api.assistance.listServices(),
        200,
        'Não foi possível carregar os serviços da assistência.',
      ),
  });
}

// @valor
export const ASSISTANCE_PAYMENT_LABEL: Record<ProviderPaymentMode, string> = {
  a_vista: 'À vista · Assistência 24h',
  mensal: 'Mensal · Financeiro',
};

/** O selo da lista de empresas. */
// @valor
export const ASSISTANCE_PAYMENT_SHORT: Record<ProviderPaymentMode, string> = {
  a_vista: 'à vista',
  mensal: 'mensal',
};

/** Metros → "30 km", sem casa quando é inteiro. */
function km(m: number): string {
  return `${(m / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} km`;
}

/** "150.00" → "150,00", para o campo com máscara de dinheiro. */
function moneyField(column: string | null): string {
  return column === null ? '' : applyInputMask('money', column);
}

/** Quem vê e quem edita a seção: as permissões da assistência, não as do cadastro. */
export function useAssistancePermissions() {
  const { data: context } = useSessionContext();
  return {
    canView: can(context, 'assistance.providers', 'ver'),
    canEdit: can(context, 'assistance.providers', 'editar'),
  };
}

function useProviderMutation<T>(fn: (input: T) => Promise<AssistanceProvider>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: PROVIDERS_KEY });
      void client.invalidateQueries({ queryKey: COMPANIES_KEY });
    },
  });
}

/**
 * A Assistência 24h na ficha da empresa (30/10/2026): a forma de pagamento,
 * o raio e a tabela de preço.
 *
 * O prestador da assistência É a empresa — o mesmo `id` —, e por isso o que
 * era a tela de Prestadores da Assistência mora aqui. Quem vê precisa de
 * `assistance.providers:ver`; quem grava, de `:editar`. A empresa em si
 * continua sendo corrigida pelo formulário do cadastro, com
 * `claims.companies`.
 */
export function CompanyAssistance({
  company,
  onDone,
}: {
  company: Company;
  onDone: () => void;
}) {
  const { canEdit } = useAssistancePermissions();
  /* Local: incluir agora mostra os preços sem esperar a lista recarregar. */
  const [inAssistance, setInAssistance] = useState(
    company.assistancePaymentMode !== null,
  );

  const provider = useQuery({
    queryKey: [...PROVIDERS_KEY, 'empresa', company.id],
    enabled: inAssistance,
    queryFn: async () =>
      ok(
        await api.assistance.getProvider({ params: { id: company.id } }),
        200,
        'Não foi possível carregar a assistência desta empresa.',
      ),
  });

  const archive = useProviderMutation(async (archived: boolean) =>
    ok(
      await api.assistance.updateProvider({
        params: { id: company.id },
        body: { archived },
      }),
      200,
      'Não foi possível mudar a empresa na assistência.',
    ),
  );

  if (!inAssistance) {
    return canEdit ? (
      <ProviderForm
        company={company}
        onDone={onDone}
        onIncluded={() => setInAssistance(true)}
      />
    ) : (
      <div className="flex flex-col gap-3">
        <Alert tone="informacao">
          Esta empresa não atende a Assistência 24h. Quem tem permissão para editar os
          prestadores da assistência pode incluí-la.
        </Alert>
        <div className="flex justify-end">
          <Button variant="discreto" onClick={onDone}>
            Fechar
          </Button>
        </div>
      </div>
    );
  }
  if (provider.isError) return <Alert tone="erro">{provider.error.message}</Alert>;
  if (!provider.data) return <p className="text-sm text-texto-suave">Carregando…</p>;

  const p = provider.data;
  const archived = p.archivedAt !== null;
  return (
    <div className="flex flex-col gap-4">
      {archived && (
        <Alert tone="alerta">
          Esta empresa foi retirada da assistência: não é oferecida em acionamento nenhum.
          O que já foi acionado e pago continua como estava.
        </Alert>
      )}
      {archive.error && <Alert tone="erro">{archive.error.message}</Alert>}

      {!canEdit ? (
        <ReadOnly provider={p} />
      ) : archived ? null : (
        <>
          <ProviderForm company={company} editing={p} onDone={onDone} />
          <Services provider={p} />
        </>
      )}

      <div className="flex justify-end gap-2">
        {canEdit && (
          <Button
            variant={archived ? 'secundario' : 'discreto'}
            loading={archive.isPending}
            onClick={() => archive.mutate(!archived)}
          >
            {archived ? 'Voltar para a assistência' : 'Tirar da assistência'}
          </Button>
        )}
        <Button variant="discreto" onClick={onDone}>
          Fechar
        </Button>
      </div>
    </div>
  );
}

/** O que a assistência tem desta empresa, para quem só vê. */
function ReadOnly({ provider }: { provider: AssistanceProvider }) {
  return (
    <dl className="grid gap-2 text-sm">
      <div>
        <dt className="text-texto-suave">Como a assistência paga</dt>
        <dd className="font-semibold">
          {ASSISTANCE_PAYMENT_LABEL[provider.paymentMode]}
        </dd>
      </div>
      <div>
        <dt className="text-texto-suave">Raio de atendimento</dt>
        <dd className="font-semibold">
          {provider.serviceRadiusM === null ? 'Sem limite' : km(provider.serviceRadiusM)}
        </dd>
      </div>
      <div>
        <dt className="text-texto-suave">Serviços que atende</dt>
        <dd>
          {provider.prices.length === 0
            ? 'Nenhum: não é oferecida em serviço nenhum.'
            : provider.prices.map((price) => price.serviceName).join(', ')}
        </dd>
      </div>
    </dl>
  );
}

/** Pôr a empresa na assistência, ou mudar a forma de pagamento e o raio. */
function ProviderForm({
  company,
  editing,
  onDone,
  onIncluded,
}: {
  company: Company;
  editing?: AssistanceProvider;
  onDone: () => void;
  /** Incluiu: a seção passa a mostrar a tabela de preço. */
  onIncluded?: () => void;
}) {
  const [paymentMode, setPaymentMode] = useState<ProviderPaymentMode | ''>(
    editing?.paymentMode ?? '',
  );
  const [radiusKm, setRadiusKm] = useState(
    editing?.serviceRadiusM ? String(editing.serviceRadiusM / 1000) : '',
  );
  const [touched, setTouched] = useState(false);
  const [saved, setSaved] = useState(false);

  const radiusNumber = radiusKm.trim() === '' ? null : Number(radiusKm.replace(',', '.'));
  const radiusValid =
    radiusNumber === null || (Number.isFinite(radiusNumber) && radiusNumber > 0);
  const serviceRadiusM = radiusNumber === null ? null : Math.round(radiusNumber * 1000);

  const save = useProviderMutation(async () =>
    editing
      ? ok(
          await api.assistance.updateProvider({
            params: { id: company.id },
            body: { paymentMode: paymentMode as ProviderPaymentMode, serviceRadiusM },
          }),
          200,
          'Não foi possível salvar.',
        )
      : ok(
          await api.assistance.createProvider({
            body: {
              companyId: company.id,
              paymentMode: paymentMode as ProviderPaymentMode,
              serviceRadiusM,
            },
          }),
          201,
          'Não foi possível incluir a empresa na assistência.',
        ),
  );

  const missing = paymentMode === '' ? ['a forma de pagamento'] : [];
  const wrongType = company.type !== 'prestador_servicos';

  if (!editing && wrongType) {
    return (
      <div className="flex flex-col gap-3">
        <Alert tone="informacao">
          Só uma empresa do tipo prestador de serviços atende a Assistência 24h. Corrija o
          tipo no cadastro antes, se for o caso.
        </Alert>
        <div className="flex justify-end">
          <Button variant="discreto" onClick={onDone}>
            Fechar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Form
      steps
      nested
      onSubmit={(e) => {
        e.preventDefault();
        setTouched(true);
        setSaved(false);
        if (missing.length === 0 && radiusValid) {
          save.mutate(undefined, {
            onSuccess: () => (editing ? setSaved(true) : onIncluded?.()),
          });
        }
      }}
    >
      {save.error && <Alert tone="erro">{save.error.message}</Alert>}
      {saved && <Alert tone="sucesso">Forma de pagamento e raio salvos.</Alert>}
      <FormSection
        icon="assistencia"
        title={editing ? 'Pagamento e raio' : 'Incluir na Assistência 24h'}
        status={{ done: 1 - missing.length, total: 1, missing }}
        descricao={
          editing
            ? 'A forma de pagamento vale para os acionamentos daqui em diante: os já feitos guardam a do dia.'
            : 'A empresa passa a ser oferecida nos serviços que marcar como atendidos. Sem coordenada no endereço, fica fora do ranking pela chegada.'
        }
      >
        <FormRow cols={[6, 6]}>
          <SearchSelect
            label="Como a assistência paga"
            required
            placeholder="Escolha…"
            options={(Object.keys(ASSISTANCE_PAYMENT_LABEL) as ProviderPaymentMode[]).map(
              (m) => ({ value: m, label: ASSISTANCE_PAYMENT_LABEL[m] }),
            )}
            value={paymentMode}
            onChange={(v) => setPaymentMode(v as ProviderPaymentMode | '')}
            error={touched && paymentMode === '' ? 'Escolha a forma de pagamento.' : null}
          />
          <Field
            label="Raio de atendimento"
            inputMode="decimal"
            suffix="km"
            placeholder="Sem limite"
            value={radiusKm}
            onChange={(e) => setRadiusKm(e.target.value)}
            help="Vazio é sem limite. Fora do raio, a empresa não é oferecida."
            error={!radiusValid ? 'Informe um número de km maior que zero.' : null}
          />
        </FormRow>
      </FormSection>
      <FormFooter>
        {!editing && (
          <Button type="button" variant="discreto" onClick={onDone}>
            Cancelar
          </Button>
        )}
        <Button type="submit" loading={save.isPending} disabled={save.isPending}>
          {editing ? 'Salvar pagamento e raio' : 'Incluir na assistência'}
        </Button>
      </FormFooter>
    </Form>
  );
}

/** Um serviço na tabela do prestador, como a tela o edita. */
interface ServiceDraft {
  on: boolean;
  pricing: AssistancePricing;
  flat: string;
  includedKm: string;
  perKm: string;
}

function draftOf(price: AssistancePriceRecord | undefined): ServiceDraft {
  if (!price)
    return { on: false, pricing: 'saida_e_km', flat: '', includedKm: '', perKm: '' };
  return {
    on: true,
    pricing: price.pricing,
    flat: moneyField(price.flatAmount),
    includedKm: price.includedM === null ? '' : String(price.includedM / 1000),
    perKm: moneyField(price.perKmAmount),
  };
}

// @valor
const PRICING_LABEL: Record<AssistancePricing, string> = {
  saida_e_km: 'Saída + KM',
  fixo: 'Fixo',
  a_combinar: 'A combinar',
};

/** A linha que o contrato aceita, ou o motivo de não aceitar. */
function writeOf(
  serviceId: number,
  d: ServiceDraft,
): AssistanceProviderServicesWrite['services'][number] | string {
  const flat = parseMoneyInput(d.flat);
  if (d.pricing === 'a_combinar') return { serviceId, pricing: 'a_combinar' };
  if (flat === null) return 'o valor de saída';
  if (d.pricing === 'fixo')
    return { serviceId, pricing: 'fixo', flatAmount: toColumn(flat) };
  const perKm = parseMoneyInput(d.perKm);
  const includedKm =
    d.includedKm.trim() === '' ? null : Number(d.includedKm.replace(',', '.'));
  if (includedKm === null || !Number.isFinite(includedKm) || includedKm < 0)
    return 'o KM de saída';
  if (perKm === null) return 'o valor do KM excedente';
  return {
    serviceId,
    pricing: 'saida_e_km',
    flatAmount: toColumn(flat),
    includedM: Math.round(includedKm * 1000),
    perKmAmount: toColumn(perKm),
  };
}

/**
 * Os serviços que o prestador atende (02/11/2026): uma linha por serviço do
 * catálogo, agrupada pelo tipo, com a forma e as três propriedades de valor do
 * Safegard — KM de saída, valor de saída e valor do KM excedente. Ter a linha
 * marcada é atender: o serviço desmarcado não oferece esta empresa no
 * acionamento. Grava a tabela inteira de uma vez.
 */
function Services({ provider }: { provider: AssistanceProvider }) {
  const catalog = useAssistanceCatalog();
  const [rows, setRows] = useState<Record<number, ServiceDraft>>(() =>
    Object.fromEntries(provider.prices.map((p) => [p.serviceId, draftOf(p)])),
  );
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const legacy = provider.prices.filter((p) => p.serviceArchived);
  const set = (id: number, patch: Partial<ServiceDraft>) => {
    setSaved(false);
    setRows((prev) => ({
      ...prev,
      [id]: { ...(prev[id] ?? draftOf(undefined)), ...patch },
    }));
  };

  const groups =
    catalog.data?.map((g) => ({
      ...g,
      services: g.services.filter((s) => s.archivedAt === null),
    })) ?? [];
  const names = new Map(groups.flatMap((g) => g.services.map((s) => [s.id, s.name])));
  const marked = groups.flatMap((g) => g.services).filter((s) => rows[s.id]?.on).length;

  const save = useProviderMutation(async () => {
    const services: AssistanceProviderServicesWrite['services'] = [];
    for (const [id, d] of Object.entries(rows)) {
      const serviceId = Number(id);
      if (!d.on || !names.has(serviceId)) continue;
      const write = writeOf(serviceId, d);
      if (typeof write === 'string')
        throw new Error(`Falta ${write} em ${names.get(serviceId)}.`);
      services.push(write);
    }
    return ok(
      await api.assistance.setProviderServices({
        params: { id: provider.id },
        body: { services },
      }),
      200,
      'Não foi possível gravar os serviços.',
    );
  });

  return (
    <section className="flex flex-col gap-3" aria-label="Serviços que atende">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-bold">Serviços que atende</h3>
        <Badge tone={marked > 0 ? 'sucesso' : 'neutro'}>
          {marked === 1 ? '1 serviço' : `${marked} serviços`}
        </Badge>
      </div>
      <p className="text-sm text-texto-suave">
        Só os serviços marcados oferecem esta empresa no acionamento. Saída + KM: a saída
        cobre os primeiros km, e cada km além dela tem um valor. Fixo: o mesmo valor,
        qualquer que seja o percurso. A combinar: sem valor na tabela — o valor de cada
        acionamento é informado depois do serviço. Mudar o valor vale daqui em diante:
        cada acionamento guarda o do dia.
      </p>
      {legacy.length > 0 && (
        <Alert tone="alerta" title="Preço de antes do catálogo">
          {legacy.map((p) => p.serviceName).join(', ')}: o serviço foi trocado por outros
          mais específicos (o reboque, por porte), e esta empresa não aparece nele até
          ganhar a linha nova. Marque o serviço certo abaixo; ao gravar, a linha antiga
          sai.
        </Alert>
      )}
      {catalog.isError && <Alert tone="erro">{catalog.error.message}</Alert>}
      {groups.map((g) => {
        const count = g.services.filter((s) => rows[s.id]?.on).length;
        return (
          <details
            key={g.serviceType}
            open={count > 0}
            className="rounded-lg border border-borda"
          >
            <summary className="flex min-h-11 cursor-pointer items-center justify-between gap-2 px-3 text-sm font-semibold">
              <span>{g.name}</span>
              <span className="text-xs font-normal text-texto-suave">
                {count === 0 ? 'Nenhum marcado' : `${count} de ${g.services.length}`}
              </span>
            </summary>
            {/*
             * Uma LINHA por serviço (02/11/2026): o serviço, a forma e os três
             * valores lado a lado, como uma tabela. O diálogo abre em `xl` para
             * isso caber; a régua é a do CONTÊINER (`@3xl`), e não a da janela —
             * o diálogo estreita antes dela, e no telefone a linha vira pilha.
             */}
            <div className="flex flex-col divide-y divide-borda border-t border-borda @container">
              {g.services.map((s) => {
                const d = rows[s.id] ?? draftOf(undefined);
                /* O adicional (o patins) soma um valor fixo ao serviço: só o valor. */
                if (s.addon)
                  return (
                    <div
                      key={s.id}
                      className="grid gap-3 p-3 @3xl:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,3fr)] @3xl:items-end"
                    >
                      <label className="flex min-h-11 items-center gap-3 text-sm font-semibold">
                        <input
                          type="checkbox"
                          className="size-5 shrink-0"
                          checked={d.on}
                          onChange={(e) =>
                            set(s.id, { on: e.target.checked, pricing: 'fixo' })
                          }
                        />
                        {s.name}
                        <Badge tone="neutro">Adicional</Badge>
                      </label>
                      {d.on && (
                        <Field
                          label="Valor do adicional"
                          mask="money"
                          prefix="R$"
                          value={d.flat}
                          onChange={(e) => set(s.id, { flat: e.target.value })}
                        />
                      )}
                    </div>
                  );
                return (
                  <div
                    key={s.id}
                    className="grid gap-3 p-3 @3xl:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)] @3xl:items-end"
                  >
                    <label className="flex min-h-11 items-center gap-3 text-sm font-semibold">
                      <input
                        type="checkbox"
                        className="size-5 shrink-0"
                        checked={d.on}
                        onChange={(e) => set(s.id, { on: e.target.checked })}
                      />
                      {s.name}
                    </label>
                    {d.on && (
                      <SearchSelect
                        label="Forma"
                        options={(Object.keys(PRICING_LABEL) as AssistancePricing[]).map(
                          (value) => ({ value, label: PRICING_LABEL[value] }),
                        )}
                        value={d.pricing}
                        onChange={(v) =>
                          set(s.id, {
                            pricing: (v || 'saida_e_km') as AssistancePricing,
                          })
                        }
                      />
                    )}
                    {/* As três propriedades do Safegard, na mesma linha da forma. */}
                    {d.on && d.pricing === 'saida_e_km' && (
                      <>
                        <Field
                          label="KM de saída"
                          inputMode="decimal"
                          suffix="km"
                          value={d.includedKm}
                          onChange={(e) => set(s.id, { includedKm: e.target.value })}
                        />
                        <Field
                          label="Valor de saída"
                          mask="money"
                          prefix="R$"
                          value={d.flat}
                          onChange={(e) => set(s.id, { flat: e.target.value })}
                        />
                        <Field
                          label="Valor do KM excedente"
                          mask="money"
                          prefix="R$"
                          value={d.perKm}
                          onChange={(e) => set(s.id, { perKm: e.target.value })}
                        />
                      </>
                    )}
                    {d.on && d.pricing === 'fixo' && (
                      <Field
                        label="Valor"
                        mask="money"
                        prefix="R$"
                        value={d.flat}
                        onChange={(e) => set(s.id, { flat: e.target.value })}
                      />
                    )}
                    {d.on && d.pricing === 'a_combinar' && (
                      <p className="flex min-h-11 items-center text-sm text-texto-suave @3xl:col-span-3">
                        Sem valor na tabela: informado depois do serviço.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </details>
        );
      })}
      {(error || save.error) && <Alert tone="erro">{error ?? save.error!.message}</Alert>}
      {saved && <Alert tone="sucesso">Serviços gravados.</Alert>}
      <div className="flex justify-end">
        <Button
          loading={save.isPending}
          disabled={save.isPending || !catalog.data}
          onClick={() => {
            setError(null);
            save.mutate(undefined, { onSuccess: () => setSaved(true) });
          }}
        >
          Gravar serviços
        </Button>
      </div>
    </section>
  );
}
